import { Box, Typography } from "@mui/material";
import {
  ContactSupportButton,
  UpgradeSubscriptionButton,
} from "components/action-buttons/action-buttons";
import { Reason } from "data/models";
import React from "react";
import { PermissionsKey } from "./permission-keys";

// These permission reasons are in priority order. The first triggered reason will be shown to the user first.
export const PermissionsReasons = {
  FEATURE_DISABLED: "feature_disabled",
  EXTERNALLY_MANAGED: "externally_managed",
  UPGRADE_REQUIRED: "upgrade_required",
} as const satisfies Record<string, Reason>;

const defaultMessageTemplate = (content: String, action: React.ReactNode) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography>{content}</Typography>
      </Box>
      {action}
    </Box>
  );
};

const externallyManagedMessage = (
  permissionKey: PermissionsKey
): React.ReactNode => {
  return defaultMessageTemplate(
    `Your subscription is managed as part of your contract. 
    For more details, please refer to your contract or contact support regarding ${permissionKey.resourceDisplay}.`,
    <ContactSupportButton />
  );
};

const upgradeRequiredMessage = (
  permissionKey: PermissionsKey
): React.ReactNode => {
  return defaultMessageTemplate(
    `You do not have access to ${permissionKey.resourceDisplay} as part of your subscription. 
    Please upgrade your subscription to gain access.`,
    <UpgradeSubscriptionButton />
  );
};

const featureDisabledMessage = (
  permissionKey: PermissionsKey
): React.ReactNode => {
  return defaultMessageTemplate(
    `The ability to ${permissionKey.actionDisplay} ${permissionKey.resourceDisplay} is currently disabled. 
    Please contact support for more details.`,
    <ContactSupportButton />
  );
};

// The default permission reason messages shown to the user
export const PermissionReasonMessages: Record<
  Reason,
  (permissionKey: PermissionsKey) => React.ReactNode
> = {
  [PermissionsReasons.FEATURE_DISABLED]: featureDisabledMessage,
  [PermissionsReasons.EXTERNALLY_MANAGED]: externallyManagedMessage,
  [PermissionsReasons.UPGRADE_REQUIRED]: upgradeRequiredMessage,
};

// The default permission reason message strings shown to the user
export const PermissionReasonMessagesString: Record<Reason, String> = {
  [PermissionsReasons.FEATURE_DISABLED]: "This feature has been disabled.",
  [PermissionsReasons.EXTERNALLY_MANAGED]:
    "This feature is managed by support as part of your subscription.",
  [PermissionsReasons.UPGRADE_REQUIRED]:
    "This feature requires a subscription upgrade.",
};
